<template>
  <div class="reportDashboard">
    <ReportHeader class="header" heading="Nøkkeltall" :lastUpdate="`Sist oppdatert: ${lastUpdate}`" />
    <div class="reportDashboard__grid mt-5">
      <!-- Cashflow -->
      <DashboardCard
        :heading="translate('reports.dashboard.Payments')"
        iconClass="budicon-credit-card"
        :loading="loading"
        :showMessage="showWarningMessage"
        :message="warningMessage"
      >
        <DashboardCashflowSummary />
      </DashboardCard>
      <!-- Available objects -->
      <DashboardCard
        :heading="translate('reports.dashboard.Rental objects')"
        iconClass="budicon-apartment-a"
        :loading="loading"
        :showMessage="showWarningMessage"
        :message="warningMessage"
      >
        <DashboardAvailableObjectsSummary />
      </DashboardCard>
      <div class="reportDashboard__grid__flex">
        <!-- Tenants -->
        <DashboardCard
          :heading="translate('reports.dashboard.Tenancy')"
          iconClass="budicon-user"
          :loading="loading"
          :showMessage="showWarningMessage"
          :message="warningMessage"
        >
          <DashboardTenantsSummary />
        </DashboardCard>
        <!-- Invoice status -->
        <DashboardCard
          class="invoice-status-summary"
          :heading="translate('reports.dashboard.Invoice status')"
          iconClass="budicon-document-page"
          :loading="loading"
          :showMessage="showWarningMessage"
          :message="warningMessage"
        >
          <DashboardInvoiceStatusSummary />
        </DashboardCard>
      </div>
      <div>
        <!-- Payment deviation -->
        <DashboardCard
          :heading="translate('reports.dashboard.Payment discrepancies')"
          iconClass="budicon-hourglass"
          :loading="loading"
          :showMessage="showWarningMessage"
          :message="warningMessage"
        >
          <DashboardPaymentDeviationSummary />
        </DashboardCard>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import ReportHeader from '@/components/reports/ReportHeader';
import DashboardCard from '@/components/DashboardCard';
import DashboardCashflowSummary from '@/components/reports/dashboard/DashboardCashflowSummary';
import DashboardAvailableObjectsSummary from '@/components/reports/dashboard/DashboardAvailableObjectsSummary';
import DashboardTenantsSummary from '@/components/reports/dashboard/DashboardTenantsSummary';
import DashboardPaymentDeviationSummary from '@/components/reports/dashboard/DashboardPaymentDeviationSummary';
import DashboardInvoiceStatusSummary from '@/components/reports/dashboard/DashboardInvoiceStatusSummary';
import moment from 'moment';
import i18nCommon from '@modules/i18n/mixins/Common';

export default {
  name: 'ReportDashboard',

  mixins: [i18nCommon],

  components: {
    ReportHeader,
    DashboardCard,
    DashboardCashflowSummary,
    DashboardAvailableObjectsSummary,
    DashboardTenantsSummary,
    DashboardPaymentDeviationSummary,
    DashboardInvoiceStatusSummary,
  },

  props: {
    landlordId: [String, Number],
  },

  computed: {
    ...mapState('reports', {
      selectedProfiles: state => state.selectedProfiles,
      loading: state => state.dashboard.loading,
    }),
    ...mapGetters('reports', ['getDashboardDataPresent', 'getDashboardGenerated']),
    anyProfileSelected() {
      return this.selectedProfiles.length > 0;
    },
    lastUpdate() {
      return this.anyProfileSelected && this.getDashboardDataPresent
        ? moment.utc(this.getDashboardGenerated).local().format('DD.MM.YY [kl.] HH:mm')
        : '';
    },
    showWarningMessage() {
      return !this.anyProfileSelected || !this.getDashboardDataPresent;
    },
    warningMessage() {
      let message = null;
      if (!this.anyProfileSelected) {
        message = 'Du må velge minst en utleieprofil.';
      } else if (!this.getDashboardDataPresent) {
        message = 'Ingen data funnet for valgte utleiere.';
      }
      return message;
    },
  },

  watch: {
    selectedProfiles: {
      async handler(newValue) {
        if (this.selectedProfiles.length > 0) {
          try {
            await this.fetchDashboardData(newValue.map(x => x.id));
          } catch (error) {
            this.$toast('Kunne ikke hente nøkkeltall.', 'error');
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapMutations('reports', ['setSelectedProfiles']),
    ...mapActions('reports', ['fetchDashboardData']),
  },
};
</script>

<style scoped lang="scss">
.reportDashboard {
  padding: 3rem;
}
.reportDashboard__grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
}
.reportDashboard__grid__flex {
  display: flex;
  flex-direction: column;
  gap: 1rem 0rem;
}

@media (min-width: 1000px) {
  .reportDashboard {
    padding: 3rem;
  }
  .reportDashboard__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  .reportDashboard__grid__flex {
    display: flex;
    flex-direction: column;
    gap: 1rem 0rem;
  }
}
</style>
